import Slider from '@mui/material/Slider';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  IFilterSliderConstant,
  priceSliderValueMapping,
} from '../../../../../../../constants/filter/slider/filterSliderConstant';
import { FilterOptionType, FilterOptionValueType } from '../../../../../../../interfaces/Filter.interfaces';
import { formatPrice } from '../../../../../../../utils/formatter/formatPrice.util';

interface FilterSliderProps {
  options: FilterOptionType[];
  handleChange: (newOptionValues: FilterOptionValueType[]) => void;
  sliderConstant: IFilterSliderConstant;
  // 슬라이더 위치와 표시 값이 다른 필터에서 미리보기 문구를 직접 만들 때 사용
  formatValuePreviewText?: (sliderValues: number[]) => string;
  // 미리보기 문구를 슬라이더 밖에서 따로 보여 줄 때 사용
  isValuePreviewHidden?: boolean;
  isDisabled?: boolean;
}

export const FilterSlider = ({
  options,
  handleChange,
  sliderConstant,
  formatValuePreviewText,
  isValuePreviewHidden,
  isDisabled,
}: FilterSliderProps) => {
  const defaultStartValue = useMemo<number>(() => {
    return Number(options.find((option) => option.optionName === 'min')?.value);
  }, [options]);

  const defaultEndValue = useMemo<number>(() => {
    return Number(options.find((option) => option.optionName === 'max')?.value);
  }, [options]);

  const [sliderValues, setSliderValues] = useState<number[]>([defaultStartValue, defaultEndValue]);

  // 일반 슬라이더 값 미리보기 텍스트
  const sliderValuePreviewText = useMemo<string>(() => {
    const currentSliderStartValue = sliderValues[0];
    const currentSliderEndValue = sliderValues[1];

    const isStartValueEqualToMin = currentSliderStartValue === sliderConstant.min;
    const isEndValueEqualToMax = currentSliderEndValue === sliderConstant.max;

    const { prefix } = sliderConstant;

    if (isStartValueEqualToMin && isEndValueEqualToMax) {
      return '전체';
    }

    if (isStartValueEqualToMin) {
      return `${currentSliderEndValue.toLocaleString()}${prefix} 이하`;
    }

    if (isEndValueEqualToMax) {
      return `${currentSliderStartValue.toLocaleString()}${prefix} 이상`;
    }

    return `${currentSliderStartValue}${prefix}~${currentSliderEndValue}${prefix}`;
  }, [sliderValues, sliderConstant]);

  // 가격 슬라이더 값 미리보기 텍스트
  const priceSliderValuePreviewText = useMemo<string>(() => {
    // 표시 값을 실제 값으로 변환
    const currentSliderStartValue = priceSliderValueMapping[sliderValues[0]] ?? sliderValues[0];
    const currentSliderEndValue = priceSliderValueMapping[sliderValues[1]] ?? sliderValues[1];

    const isStartValueEqualToMin = sliderValues[0] === sliderConstant.min;
    const isEndValueEqualToMax = sliderValues[1] === sliderConstant.max;

    if (isStartValueEqualToMin && isEndValueEqualToMax) {
      return '전체';
    }

    if (isStartValueEqualToMin) {
      return `${formatPrice(currentSliderEndValue)}원 이하`;
    }

    if (isEndValueEqualToMax) {
      return `${formatPrice(currentSliderStartValue)}원 이상`;
    }

    return `${formatPrice(currentSliderStartValue)}원~${formatPrice(currentSliderEndValue)}원`;
  }, [sliderValues, sliderConstant]);

  // 슬라이더 타입에 따라 적절한 미리보기 텍스트 선택
  const displayValuePreviewText = useMemo<string>(() => {
    if (formatValuePreviewText) {
      return formatValuePreviewText(sliderValues);
    }
    // 가격 슬라이더인 경우 priceSliderValuePreviewText 사용
    if (sliderConstant.prefix === '원') {
      return priceSliderValuePreviewText;
    }
    // 그 외의 경우 일반 sliderValuePreviewText 사용
    return sliderValuePreviewText;
  }, [
    sliderValuePreviewText,
    priceSliderValuePreviewText,
    sliderConstant.prefix,
    formatValuePreviewText,
    sliderValues,
  ]);

  const handleSliderChange = (event: Event, newValue: number | number[], activeThumb: number) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    const isLeftThumbChanged = activeThumb === 0;

    const currentSliderStartValue = sliderValues[0];
    const currentSliderEndValue = sliderValues[1];

    const newSliderStartValue = newValue[0];
    const newSliderEndValue = newValue[1];

    if (isLeftThumbChanged) {
      const newValues = [
        Math.min(newSliderStartValue, currentSliderEndValue - sliderConstant.minDistance),
        currentSliderEndValue,
      ];
      setSliderValues(newValues);
      handleChange(newValues);
    } else {
      const newValues = [
        currentSliderStartValue,
        Math.max(newSliderEndValue, currentSliderStartValue + sliderConstant.minDistance),
      ];
      setSliderValues(newValues);
      handleChange(newValues);
    }
  };

  useEffect(() => {
    setSliderValues([defaultStartValue, defaultEndValue]);
  }, [options]);

  return (
    <SliderWrapper isDisabled={isDisabled}>
      {!isValuePreviewHidden && (
        <SliderValuePreviewSection>
          <SliderValuePreviewText isDisabled={isDisabled}>{displayValuePreviewText}</SliderValuePreviewText>
        </SliderValuePreviewSection>
      )}
      <Slider
        marks={sliderConstant.marks}
        step={null}
        value={sliderValues}
        valueLabelDisplay={'off'}
        min={sliderConstant.min}
        max={sliderConstant.max}
        onChange={handleSliderChange}
        disableSwap
        disabled={isDisabled}
      />
    </SliderWrapper>
  );
};

const SliderWrapper = styled.div<{ isDisabled?: boolean }>`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  position: relative;

  gap: 10px;

  padding: 16px 40px;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
`;

const SliderValuePreviewSection = styled.div`
  height: 48px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  position: absolute;
  top: -48px;
  right: 0px;

  padding-right: 24px;
`;

const SliderValuePreviewText = styled.span<{ isDisabled?: boolean }>`
  color: ${(props) => (props.isDisabled ? '#A7A9B6' : 'var(--Light-Scheme-Color-primary-blue, #196eff)')};

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;
